import React from 'react'
import { MapPinIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon, StarIcon, EyeIcon, HandRaisedIcon } from '@heroicons/react/24/outline'
import { Company, API_URL } from '@/services/api'
import CustomLink from '@/components/common/CustomLink'

interface CompanyCardProps {
  company: Company
  showContactInfo?: boolean
  detailUrl?: string // Optional: URL with search context preserved
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  showContactInfo = false,
  detailUrl
}) => {
  // Use provided detailUrl or fallback to basic slug URL
  const companyUrl = detailUrl || `/companies/${company.slug}`
  // Helper function to convert relative path to full URL
  const getImageUrl = (url: string | null | undefined): string | null => {
    if (!url) return null
    // If already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Convert relative path to full URL
    return `${API_URL}${url}`
  }

  const handleContactClick = (type: 'phone' | 'email', value: string) => {
    // Track contact click
    console.log(`Contact clicked: ${type} - ${value}`)

    if (type === 'phone') {
      window.open(`tel:${value}`, '_self')
    } else if (type === 'email') {
      window.open(`mailto:${value}`, '_self')
    }
  }

  const formatPhoneNumber = (phone: string | null | undefined) => {
    // Handle null, undefined, or empty phone numbers
    if (!phone) return ''

    // Ensure it's a string
    if (typeof phone !== 'string') return ''

    // Format phone number for display
    return phone.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3')
  }

  const getCompanySizeText = (size: string) => {
    switch (size) {
      case 'SMALL': return 'เล็ก'
      case 'MEDIUM': return 'กลาง'
      case 'LARGE': return 'ใหญ่'
      default: return ''
    }
  }

  return (
    <div className="group relative bg-white rounded-2xl shadow-sm border border-neutral-200 hover:border-primary-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full">
      {/* Featured Badge */}
      {company.isFeatured && (
        <div className="absolute top-0 right-0 z-10">
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-2xl text-xs font-semibold flex items-center shadow-lg">
            <StarIcon className="w-3 h-3 mr-1" />
            แนะนำ
          </div>
        </div>
      )}

      <CustomLink to={companyUrl} className="block flex-1 flex flex-col">
        <div className="p-6 flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-start space-x-4 mb-5">
            {/* Company Logo */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 rounded-xl overflow-hidden ring-2 ring-primary-100 group-hover:ring-primary-200 transition-all duration-300">
                {getImageUrl(company.logoUrl) ? (
                  <img
                    src={getImageUrl(company.logoUrl)!}
                    alt={company.nameTh}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to default if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.classList.add('bg-gradient-to-br', 'from-primary-50', 'to-primary-100', 'flex', 'items-center', 'justify-center');
                        parent.innerHTML = '<svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>';
                      }
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
                    <BuildingOfficeIcon className="w-8 h-8 text-primary-600" />
                  </div>
                )}
              </div>
              {company.isVerified && (
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>

            {/* Company Info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-neutral-900 mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                {company.nameTh}
              </h3>

              {company.nameEn && (
                <p className="text-sm text-neutral-500 font-english mb-2 line-clamp-1">{company.nameEn}</p>
              )}

              <div className="flex items-center text-sm text-neutral-600 space-x-3">
                <div className="flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-1 text-neutral-400" />
                  <span className="truncate">{company.province}</span>
                </div>
                <div className="flex items-center text-xs">
                  <BuildingOfficeIcon className="w-3 h-3 mr-1 text-neutral-400" />
                  <span className="truncate">ขนาด{getCompanySizeText(company.companySize)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description - Fixed height slot */}
          <div className="mb-4 h-10">
            {company.description && (
              <p className="text-neutral-700 text-sm leading-relaxed line-clamp-2">
                {company.description}
              </p>
            )}
          </div>

          {/* Service Types */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-1.5">
              {company.serviceTypes.slice(0, 2).map((service, index) => (
                <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                  {service}
                </span>
              ))}
              {company.serviceTypes.length > 2 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
                  +{company.serviceTypes.length - 2} อื่นๆ
                </span>
              )}
            </div>
          </div>

          {/* Service Areas - Compact Display */}
          {company.serviceAreas.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center text-xs text-neutral-500 mb-1">
                <span className="text-neutral-700 font-medium">พื้นที่บริการ:</span>
              </div>
              <div className="text-sm text-neutral-600">
                {company.serviceAreas.slice(0, 3).join(', ')}
                {company.serviceAreas.length > 3 && (
                  <span className="text-neutral-500"> และอีก {company.serviceAreas.length - 3} พื้นที่</span>
                )}
              </div>
            </div>
          )}
        </div>
      </CustomLink>

      {/* Contact Section - Always at bottom */}
      <div className="px-6 pb-6 mt-auto">
        <div className="grid grid-cols-2 gap-2">
          {/* Primary Contact - Phone */}
          {showContactInfo ? (
            company.phone1 ? (
              <button
                onClick={() => handleContactClick('phone', company.phone1)}
                className="flex items-center justify-center px-3 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <PhoneIcon className="w-4 h-4 mr-1.5" />
                โทร
              </button>
            ) : (
              <div className="flex items-center justify-center px-3 py-2.5 bg-neutral-100 text-neutral-400 rounded-lg text-sm">
                ไม่มีข้อมูล
              </div>
            )
          ) : (
            <CustomLink
              to="/login"
              className="flex items-center justify-center px-3 py-2.5 bg-neutral-300 hover:bg-neutral-400 text-neutral-700 rounded-lg text-sm font-medium transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              เข้าสู่ระบบ
            </CustomLink>
          )}

          {/* Secondary Contact - Only visible if user has permission */}
          {showContactInfo && company.lineId ? (
            <a
              href={`https://line.me/ti/p/${company.lineId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-3 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.628-.629.628M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
              </svg>
              Line
            </a>
          ) : showContactInfo && company.email ? (
            <button
              onClick={() => handleContactClick('email', company.email!)}
              className="flex items-center justify-center px-3 py-2.5 bg-neutral-600 hover:bg-neutral-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              <EnvelopeIcon className="w-4 h-4 mr-1.5" />
              อีเมล
            </button>
          ) : showContactInfo && company.website ? (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              เว็บไซต์
            </a>
          ) : !showContactInfo ? (
            <CustomLink
              to="/login"
              className="flex items-center justify-center px-3 py-2.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 rounded-lg text-sm font-medium transition-colors duration-200"
            >
              <EnvelopeIcon className="w-4 h-4 mr-1.5" />
              เข้าสู่ระบบ
            </CustomLink>
          ) : (
            <div className="flex items-center justify-center px-3 py-2.5 bg-neutral-100 text-neutral-400 rounded-lg text-sm">
              ไม่มีข้อมูล
            </div>
          )}
        </div>

        {/* Stats Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-100">
          <div className="flex items-center space-x-4 text-xs text-neutral-500">
            <div className="flex items-center">
              <EyeIcon className="w-3 h-3 mr-1" />
              <span>{company.viewCount.toLocaleString()}</span>
            </div>
            <div className="flex items-center">
              <HandRaisedIcon className="w-3 h-3 mr-1" />
              <span>{company.contactCount}</span>
            </div>
          </div>
          {company.establishedYear && (
            <span className="text-xs text-neutral-500 font-medium">ก่อตั้ง {company.establishedYear}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default CompanyCard